import React, { useRef } from "react";
import { DataTable, IconButton, TouchableRipple, useTheme } from "react-native-paper";
import { ThemeType } from "../../theme/theme";
import { Text } from "../text/text";
import { I18nManager, StyleSheet, View, Image, Pressable, Platform } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Spacer } from "../spacer/spacer";
import moment from "moment";
import { spacing } from "../../theme/spacing";
import { translate } from "../../i18n";
import { toWebUIDollars } from "../../utils/money";
import { startCase, upperCase } from "lodash";
import { FullScreenLoader } from "../new/loader/full-screen-loader";
import automationTestingLocators from "../../screens/new/utils/automation-testing-locators.json";

const constants = require("domuso-constants");
const { PaymentTypes, PaymentSourceCardTypes, PaymentStatusTypes } = constants.payment;
const { CheckType } = constants.check21;

const ITEMS_PER_PAGE = 6;

interface IDataTableParams {
  isLoading: boolean;
  headers: [string, string, string, string?];
  rows: any[];
}

export const HistoryTable = ({ isLoading, rows = [] }: IDataTableParams) => {
  const theme = useTheme() as ThemeType;
  const [page, setPage] = React.useState(0);
  const from = page * ITEMS_PER_PAGE;
  const to = (page + 1) * ITEMS_PER_PAGE;
  const styles = getStyles(theme);
  const { colors } = theme;

  const slicedRows = rows.slice(from, to);
  return isLoading ? (
    <FullScreenLoader loading={isLoading} />
  ) : (
    <DataTable>
      {rows.length ? (
        <>
          {slicedRows
            .sort((a, b) => new Date(b?.TransDate) - new Date(a?.TransDate))
            .map((row: any, index) => (
              <DataTableRow
                styles={styles}
                colors={colors}
                theme={theme}
                keyIndex={index}
                data={row}
              />
            ))}
          <Spacer size="small" />
          <CustomPagination
            page={page}
            numberOfPages={Math.ceil(rows.length / ITEMS_PER_PAGE) + 1}
            onPageChange={(p) => setPage(p)}
          />
        </>
      ) : (
        <DataTable.Row style={styles.removeBlueOutline}>
          <DataTable.Cell style={styles.removeBlueOutline}>Nothing Here Yet!</DataTable.Cell>
        </DataTable.Row>
      )}
    </DataTable>
  );
};

interface DataTableRowProps {
  keyIndex: number;
  styles: any;
  theme: any;
  data: any;
  colors: any;
}

const DataTableRow = ({ keyIndex, styles, data, colors }: DataTableRowProps) => {
  let paymentTitle;
  const iconStyle = styles.icon;
  const amount = toWebUIDollars(data.amount);
  const date = moment.utc(data.date).format("ll");
  const balanceOrStatus = data.status ? startCase(data.status.toLowerCase()) : "";

  paymentTitle = (() => {
    if (data.Notes) {
      return data.Notes;
    }
    if (upperCase(data.type) === PaymentTypes.CHARGE) return translate("homeStack.charge");
    return translate("homeStack.payment");
  })();
  const isCertifiedCheck = data.check_type === CheckType.CERTIFIED;

  const rowContent = (
    <DataTable.Row key={keyIndex} style={[styles.row, styles.removeBlueOutline]}>
      <DataTable.Cell style={[styles.firstCol, styles.removeBlueOutline]}>
        <RowIcon
          status={data.status}
          paymentType={data.type}
          paymentSourceType={data.payment_source_type}
          isCheckScan={Boolean(data.scan_check_id)}
          isCertifiedCheck={isCertifiedCheck}
          isMobile={data.is_mobile}
          cardType={data.payment_source_card_type}
          iconStyle={iconStyle}
        />
      </DataTable.Cell>
      <DataTable.Cell style={[styles.middleCol, styles.removeBlueOutline]}>
        <View>
          <Text
            style={[styles.boldText, styles.marginBelowText]}
            {...automationTestingLocators.specificReservation.history.table.paymentTitle}>
            {paymentTitle}
          </Text>
          <Text
            style={styles.smallText}
            {...automationTestingLocators.specificReservation.history.table.orderId}>
            {date} - {data.orderId}
          </Text>
        </View>
      </DataTable.Cell>
      <DataTable.Cell style={[styles.lastCol, styles.removeBlueOutline]}>
        <View>
          <Text
            style={[styles.textRightAlign, styles.marginBelowText]}
            {...automationTestingLocators.specificReservation.history.table.amount}>
            {amount}
          </Text>
          <Text
            style={[styles.textRightAlign, styles.smallText]}
            {...automationTestingLocators.specificReservation.history.table.balanceOrStatus}>
            {balanceOrStatus}
          </Text>
        </View>
      </DataTable.Cell>
    </DataTable.Row>
  );
  return (
    <Pressable
      style={({ pressed }) => [
        { backgroundColor: pressed ? colors.lightBlue : colors.white },
        styles.removeBlueOutline,
      ]}
      onPress={data.onPress}
      {...automationTestingLocators.specificReservation.history.table.rowContent}>
      {rowContent}
    </Pressable>
  );
};

interface CustomPaginationProps {
  page: number;
  numberOfPages: number;
  onPageChange: (p: number) => any;
}
const CustomPagination = ({ page, numberOfPages, onPageChange }: CustomPaginationProps) => {
  const theme = useTheme() as ThemeType;
  const boxHeight = 40;
  const styles = StyleSheet.create({
    container: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    pageButtonContainers: {
      display: "flex",
      flex: 0.8,
      marginHorizontal: theme.spacing.smaller,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
  });
  const boxStyle = {
    backgroundColor: theme.colors.palette.lightestBlue,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 5,
    width: boxHeight,
    height: boxHeight,
    borderRadius: 8,
    ...getStyles(theme).removeBlueOutline,
  };
  const buttonBoxStyle = {
    ...boxStyle,
  };

  const ArrowBox = ({ direction }: any) => {
    const isLeft = direction === "LEFT";
    const name = isLeft ? "chevron-left" : "chevron-right";
    const onPress = () => {
      const incrementer = isLeft ? -1 : 1;
      const newPage = page + incrementer;
      if (newPage < 0 || newPage >= numberOfPages - 1) {
        return false;
      }
      onPageChange(newPage);
    };
    return (
      <TouchableRipple style={boxStyle} onPress={onPress}>
        <View pointerEvents="none">
          <IconButton
            accessibilityLabel={
              isLeft
                ? translate("componentStatic.previousButton")
                : translate("componentStatic.nextButton")
            }
            icon={({ size, color }) => {
              return (
                <MaterialCommunityIcons
                  name={name}
                  size={size}
                  color={color}
                  direction={I18nManager.isRTL ? "rtl" : "ltr"}
                />
              );
            }}
            color={theme.colors.text}
          />
        </View>
      </TouchableRipple>
    );
  };
  const numsToShow = [1, 2, 3, 4]
    .map((num) => {
      const nextPage = page + num;
      if (nextPage < numberOfPages) {
        return nextPage;
      }
      return false;
    })
    .filter(Boolean);
  const NumberBox = ({ num, isSelected }: any) => {
    return (
      <TouchableRipple
        onPress={() => {
          onPageChange(num - 1);
        }}
        style={[
          buttonBoxStyle,
          isSelected
            ? {
                backgroundColor: theme.colors.white,
                borderColor: theme.colors.darkBg,
                borderWidth: 2,
              }
            : {},
        ]}>
        <Text style={{ fontSize: theme.fontSize.large }}>{num}</Text>
      </TouchableRipple>
    );
  };
  return (
    <View style={styles.container}>
      <ArrowBox direction="LEFT" />
      {numsToShow.map((num) => (
        <NumberBox num={num} key={num} isSelected={num === page + 1} />
      ))}
      <ArrowBox direction="RIGHT" />
    </View>
  );
};

const getStyles = (theme: ThemeType) => {
  const { fontSize, colors, iconSize } = theme;
  return StyleSheet.create({
    row: {
      paddingHorizontal: spacing.smaller,
      marginHorizontal: 0,
      marginVertical: spacing.tiny,
      paddingVertical: spacing.small,
      minHeight: spacing.small,
    },
    firstCol: { flex: 0.3, alignItems: "center", justifyContent: "flex-start" },
    middleCol: { flex: 1.6, alignItems: "center" },
    lastCol: { flex: 1.4, justifyContent: "flex-end" },
    icon: {
      width: iconSize.small,
      height: iconSize.small,
    },
    loading: {
      paddingVertical: spacing.large,
    },
    text: {
      fontSize: fontSize.large,
      color: colors.darkGrey,
      textAlign: "center",
    },
    boldText: {
      fontWeight: "bold",
    },
    marginBelowText: { marginBottom: spacing.smaller },
    smallText: {
      fontSize: fontSize.small,
      lineHeight: fontSize.small,
      color: colors.darkGrey,
    },
    underline: {
      textDecorationLine: "underline",
    },
    textRightAlign: { textAlign: "right" },
    removeBlueOutline: {
      ...Platform.select({
        web: {
          outlineStyle: "none",
        } as any,
      }),
    },
  });
};

interface RowIconProps {
  status: string;
  paymentType: string;
  cardType: string;
  paymentSourceType: string;
  isCheckScan: boolean;
  isCertifiedCheck: boolean;
  isMobile: boolean;
  iconStyle: any;
}

const RowIcon = ({
  status,
  paymentType,
  cardType,
  paymentSourceType,
  isCheckScan,
  isCertifiedCheck,
  isMobile,
  iconStyle,
}: RowIconProps) => {
  let icon;

  const getIconByStatus = () => {
    switch (status) {
      case PaymentStatusTypes.REFUNDED:
      case PaymentStatusTypes.RETURNED:
        return (
          <Image source={require("../../../assets/table-icons/return.png")} style={iconStyle} />
        );
      case PaymentStatusTypes.REJECTED:
        return (
          <Image source={require("../../../assets/table-icons/reject.png")} style={iconStyle} />
        );
      case PaymentStatusTypes.VOID:
        return <Image source={require("../../../assets/table-icons/void.png")} style={iconStyle} />;
      default:
        return null;
    }
  };

  const getIconByCardType = () => {
    switch (cardType) {
      case PaymentSourceCardTypes.VISA:
        return <Image source={require("../../../assets/table-icons/visa.png")} style={iconStyle} />;
      case PaymentSourceCardTypes.DISCOVER:
        return (
          <Image source={require("../../../assets/table-icons/discover.png")} style={iconStyle} />
        );
      case PaymentSourceCardTypes.MASTER_CARD:
        return (
          <Image source={require("../../../assets/table-icons/mastercard.png")} style={iconStyle} />
        );
      case PaymentSourceCardTypes.AMEX:
        return <Image source={require("../../../assets/table-icons/amex.png")} style={iconStyle} />;
      default:
        return (
          <Image source={require("../../../assets/table-icons/payment.png")} style={iconStyle} />
        );
    }
  };

  icon = getIconByStatus();
  if (!icon) {
    if (paymentType === PaymentTypes.DOMUSO_CASH) {
      icon = <Image source={require("../../../assets/table-icons/dcash.png")} style={iconStyle} />;
    } else if (paymentType === PaymentTypes.RENT) {
      if (isCheckScan && isMobile) {
        icon = (
          <Image
            source={require("../../../assets/table-icons/mobile_checkscan.png")}
            style={iconStyle}
          />
        );
      } else if (isCheckScan) {
        icon = (
          <Image
            source={require("../../../assets/table-icons/manager_checkscan.png")}
            style={iconStyle}
          />
        );
      }
    } else if (upperCase(paymentType) === PaymentTypes.CHARGE) {
      icon = <Image source={require("../../../assets/table-icons/charge.png")} style={iconStyle} />;
    }
  }
  if (!icon) icon = getIconByCardType();
  return icon;
};
